import * as THREE from '/assets/scripts/three.module.js';
import { cycleAllowedOrientation, nextCoolerQuarterTurn, orientationClassFromObject3D } from '../spatial/orientation-profiles.js?v=20260817-relbridge9';
import { calculateCleanBox } from '../scene/object-geometry.js?v=20260817-relbridge9';

function setEmissiveColorRecursive(object, hexColor) {
  if (object?.material) {
    const materials=Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach(material => {
      if (material?.emissive && typeof material.emissive.setHex === 'function') material.emissive.setHex(hexColor);
    });
  }
  for (const child of object?.children || []) setEmissiveColorRecursive(child, hexColor);
}

export function createObjectInteractionController({
  getActiveBoxes,
  getActiveCase,
  getCamera,
  getRenderer,
  getControls,
  getRaycaster,
  getMouse,
  getEngineMode,
  proposePositionAxis,
  checkAndAttachToMotherboardSocket,
  checkAndSnapFanToRadiator,
  updateAttachedMoboChildren,
  realignAllComponents,
  markRenderDirty,
  markSpatialDirty
} = {}) {
  let selectedObject = null;
  let isShiftPressed = false;
  let lastTapTime = 0;
  const dragPlane = new THREE.Plane();
  const planeIntersection = new THREE.Vector3();
  const dragOffset = new THREE.Vector3();
  let bound = false;

  function activeBoxes() { return getActiveBoxes?.() || []; }
  function hasMotherboard() { return activeBoxes().some(item => item?.isComponent && item?.trackType === 'motherboard'); }
  function isPinnedFan(comp) {
    return comp?.trackType==='fan' && (
      !!comp?.attachedToCaseUid ||
      (!!comp?.attachedToRadiatorUid && !comp?.fanAssemblyDetached)
    );
  }

  function updateSliderUi(box) {
    if (!box) return;
    const modelHeight = box.nativePhysicalHeight || box.totalModelHeight || 0;
    const xSlider = document.querySelector(`#item_${box.uid} .sff-x-range-node`);
    const ySlider = document.querySelector(`#item_${box.uid} .sff-y-range-node`);
    const zSlider = document.querySelector(`#item_${box.uid} .sff-z-range-node`);
    const xLabel = document.getElementById(`xval_${box.uid}`);
    const yLabel = document.getElementById(`hval_${box.uid}`);
    const zLabel = document.getElementById(`zval_${box.uid}`);

    if (box.isAirCooler && hasMotherboard()) {
      if (xSlider) xSlider.value = box.userOffsetX || 0;
      if (ySlider) ySlider.value = box.userOffsetY || 0;
      if (zSlider) zSlider.value = box.userOffsetZ || 0;
      if (xLabel) xLabel.textContent = `${(box.userOffsetX || 0).toFixed(1)}mm`;
      if (yLabel) yLabel.textContent = `${(box.userOffsetY || 0).toFixed(1)}mm`;
      if (zLabel) zLabel.textContent = `${(box.userOffsetZ || 0).toFixed(1)}mm`;
      return;
    }

    const displayPos=(box.trackType==='fan'&&box.attachedToRadiatorUid&&!box.fanAssemblyDetached)
      ? box.getWorldPosition(new THREE.Vector3()) : box.position;
    if (xSlider) xSlider.value = displayPos.x;
    if (ySlider) ySlider.value = displayPos.y - (modelHeight / 2);
    if (zSlider) zSlider.value = displayPos.z;
    if (xLabel) xLabel.textContent = `${displayPos.x.toFixed(1)}mm`;
    if (yLabel) yLabel.textContent = `${(displayPos.y - (modelHeight / 2)).toFixed(1)}mm`;
    if (zLabel) zLabel.textContent = `${displayPos.z.toFixed(1)}mm`;
  }

  function adjustComponentX(uid, val) {
    const comp=activeBoxes().find(item => item.uid === uid);
    if (!comp) return;
    if (isPinnedFan(comp)) return;
    if (comp.isAirCooler && hasMotherboard()) {
      comp.userOffsetX=parseFloat(val);
      checkAndAttachToMotherboardSocket?.(comp);
    } else {
      comp.attachedToMoboUid=null;
      proposePositionAxis?.(comp,'x',parseFloat(val),isShiftPressed);
    }
    updateSliderUi(comp);
  }

  function adjustComponentHeight(uid, val) {
    const comp=activeBoxes().find(item => item.uid === uid);
    if (!comp) return;
    if (isPinnedFan(comp)) return;
    if (comp.isAirCooler && hasMotherboard()) {
      comp.userOffsetY=parseFloat(val);
      checkAndAttachToMotherboardSocket?.(comp);
    } else {
      comp.attachedToMoboUid=null;
      proposePositionAxis?.(comp,'y',(comp.totalModelHeight / 2) + parseFloat(val),isShiftPressed);
    }
    updateSliderUi(comp);
  }

  function adjustComponentZ(uid, val) {
    const comp=activeBoxes().find(item => item.uid === uid);
    if (!comp) return;
    if (isPinnedFan(comp)) return;
    if (comp.isAirCooler && hasMotherboard()) {
      comp.userOffsetZ=parseFloat(val);
      checkAndAttachToMotherboardSocket?.(comp);
    } else {
      comp.attachedToMoboUid=null;
      proposePositionAxis?.(comp,'z',parseFloat(val),isShiftPressed);
    }
    updateSliderUi(comp);
  }

  function stepComponentAxis(uid, axis, direction) {
    const comp=activeBoxes().find(item => item.uid === uid);
    if (!comp) return;
    if (isPinnedFan(comp)) return;
    const stepSize=isShiftPressed ? 0.1 : 1.0;
    if (comp.isAirCooler && hasMotherboard()) {
      if (axis === 'x') adjustComponentX(uid, (comp.userOffsetX || 0) + direction * stepSize);
      else if (axis === 'y') adjustComponentHeight(uid, (comp.userOffsetY || 0) + direction * stepSize);
      else if (axis === 'z') adjustComponentZ(uid, (comp.userOffsetZ || 0) + direction * stepSize);
      return;
    }
    let currentVal=comp.position[axis];
    if (axis === 'y') currentVal -= comp.totalModelHeight / 2;
    const targetVal=currentVal + direction * stepSize;
    if (axis === 'y') adjustComponentHeight(uid,targetVal);
    else if (axis === 'x') adjustComponentX(uid,targetVal);
    else if (axis === 'z') adjustComponentZ(uid,targetVal);
  }

  function toggleFeetHeightSelector(isChecked) {
    const target=document.getElementById('sff-feet-height-container');
    if (target) target.style.display=isChecked ? 'flex' : 'none';
  }

  function adjustCameraTarget() {
    const controls=getControls?.();
    if (!controls) return;
    const xSlider=document.getElementById('cam-target-x');
    const ySlider=document.getElementById('cam-target-y');
    if (xSlider) controls.target.x=parseFloat(xSlider.value);
    if (ySlider) controls.target.y=parseFloat(ySlider.value);
    controls.update();
  }

  function toggleXRayMode(uid, btnElement) {
    const box=activeBoxes().find(item => item.uid === uid);
    if (!box) return;
    box.isXRay=!box.isXRay;
    const applyOpacity=(obj,opacity)=>{
      if (obj !== box && obj.isComponent) return;
      if (obj.material) {
        const materials=Array.isArray(obj.material) ? obj.material : [obj.material];
        materials.forEach(material => { material.transparent=true; material.opacity=opacity; });
      }
      for (const child of obj.children || []) if (!child.isComponent) applyOpacity(child,opacity);
    };
    if (box.isXRay) {
      applyOpacity(box,0.20);
      if (btnElement) btnElement.style.color='#38bdf8';
    } else {
      applyOpacity(box,box.isCase ? 0.40 : 1.0);
      if (btnElement) btnElement.style.color='var(--sub-text)';
    }
    markRenderDirty?.();
  }

  function updateMouseCoordinates(event) {
    const renderer=getRenderer?.();
    const mouse=getMouse?.();
    if (!renderer || !mouse) return;
    const rect=renderer.domElement.getBoundingClientRect();
    mouse.x=((event.clientX-rect.left)/rect.width)*2-1;
    mouse.y=-((event.clientY-rect.top)/rect.height)*2+1;
  }

  function rotateThreeDimensions(obj, direction = 1) {
    if (!obj || isPinnedFan(obj)) return;
    const targetCase=getActiveCase?.();
    const activePlan=targetCase?.spatialPlan || null;
    let layout=activePlan?.layoutFamily || targetCase?.nativeSpecs?.style || 'reference';
    if(obj.trackType==='psu' && activePlan?.layoutFamily==='sandwich'
      && (activePlan?.effective?.modeTraits?.uprightSandwich===true || activePlan?.effective?.physicalLayoutId==='vertical_sandwich')) layout='vertical sandwich';
    if (obj.isAirCooler && hasMotherboard()) {
      nextCoolerQuarterTurn(obj,direction);
      checkAndAttachToMotherboardSocket?.(obj);
      obj.relationalOrientationClass=orientationClassFromObject3D(obj);
      obj.wireframeHelper?.update?.();
      updateSliderUi(obj);
      return;
    }
    const oldMinY=obj.isCase ? (obj.addedFeetHeight||0) : calculateCleanBox(obj).min.y;
    const next=cycleAllowedOrientation(obj,layout,direction,obj.isComponent?(targetCase?.spatialPlan?.transformClass||0):0);
    if (!next) return;
    if (obj.isComponent && Number.isInteger(next.id)) {
      obj.preferredOrientationClass=next.id;
      obj.preferredOrientationTopology=targetCase?.spatialPlan?.topologyId || null;
    }
    if (getEngineMode?.()==='simulator' && obj.isComponent) realignAllComponents?.();
    else {
      obj.position.y=0;
      obj.updateMatrixWorld(true);
      const cleanBox=calculateCleanBox(obj);
      obj.totalModelHeight=cleanBox.max.y-cleanBox.min.y;
      obj.position.y=oldMinY-cleanBox.min.y;
      obj.updateMatrixWorld(true);
    }
    updateAttachedMoboChildren?.(obj);
    obj.wireframeHelper?.update?.();
    updateSliderUi(obj);
  }

  function flipCaseOrientation(uid) {
    const box=activeBoxes().find(item => item.uid === uid);
    if (box) rotateThreeDimensions(box);
  }

  function onMouseWheelHeightAdjust(event) {
    if (!selectedObject || selectedObject.isLockedToCase || isPinnedFan(selectedObject)) return;
    event.preventDefault();
    const stepSize=isShiftPressed ? 0.5 : 5.0;
    const targetHeight=Math.max(0,(selectedObject.position.y-(selectedObject.totalModelHeight/2))+(event.deltaY>0?-stepSize:stepSize));
    adjustComponentHeight(selectedObject.uid,targetHeight);
  }

  function onDragPointerDown(event) {
    const controls=getControls?.();
    const raycaster=getRaycaster?.();
    const camera=getCamera?.();
    if (!raycaster || !camera) return;
    if (event.pointerType === 'touch' && !event.isPrimary) { onDragPointerUp(); return; }
    if (event.pointerType === 'touch') {
      const currentTime=Date.now();
      const tapLength=currentTime-lastTapTime;
      if (tapLength < 300 && tapLength > 0) {
        updateMouseCoordinates(event);
        raycaster.setFromCamera(getMouse?.(),camera);
        const intersects=raycaster.intersectObjects(activeBoxes(),true);
        if (intersects.length) {
          event.preventDefault();
          let root=intersects[0].object;
          while (root.parent && !activeBoxes().includes(root)) root=root.parent;
          rotateThreeDimensions(root);
          selectedObject=null;
          if (controls) controls.enabled=true;
          lastTapTime=0;
          return;
        }
      }
      lastTapTime=currentTime;
    }

    updateMouseCoordinates(event);
    raycaster.setFromCamera(getMouse?.(),camera);
    const intersects=raycaster.intersectObjects(activeBoxes(),true);
    if (!intersects.length) return;
    let root=intersects[0].object;
    while (root.parent && !activeBoxes().includes(root)) root=root.parent;
    selectedObject=root;
    if (selectedObject.isComponent && selectedObject.isLockedToCase) selectedObject=getActiveCase?.() || selectedObject;
    if (selectedObject.isAirCooler && hasMotherboard()) selectedObject=activeBoxes().find(item => item?.isComponent && item?.trackType==='motherboard') || selectedObject;
    if (controls) controls.enabled=false;
    setEmissiveColorRecursive(selectedObject,0x222222);
    selectedObject.updateMatrixWorld(true);
    markRenderDirty?.();
    const cleanBox=calculateCleanBox(selectedObject);
    selectedObject.totalModelHeight=cleanBox.max.y-cleanBox.min.y;
    dragPlane.setFromNormalAndCoplanarPoint(new THREE.Vector3(0,1,0),selectedObject.position);
    if (raycaster.ray.intersectPlane(dragPlane,planeIntersection)) dragOffset.copy(selectedObject.position).sub(planeIntersection);
  }

  function onDragPointerMove(event) {
    if (!selectedObject) return;
    const raycaster=getRaycaster?.();
    const camera=getCamera?.();
    if (!raycaster || !camera) return;
    updateMouseCoordinates(event);
    raycaster.setFromCamera(getMouse?.(),camera);
    if (raycaster.ray.intersectPlane(dragPlane,planeIntersection)) {
      const targetPosition=planeIntersection.clone().add(dragOffset);
      proposePositionAxis?.(selectedObject,'x',targetPosition.x,event.shiftKey||isShiftPressed);
      proposePositionAxis?.(selectedObject,'z',targetPosition.z,event.shiftKey||isShiftPressed);
      updateSliderUi(selectedObject);
    }
  }

  function onDragPointerUp() {
    if (selectedObject) {
      setEmissiveColorRecursive(selectedObject,0x000000);
      if (selectedObject.trackType === 'fan') {
        checkAndSnapFanToRadiator?.(selectedObject);
        updateSliderUi(selectedObject);
      }
      selectedObject=null;
      markSpatialDirty?.();
    }
    const controls=getControls?.();
    if (controls) controls.enabled=true;
  }

  function onRightClickRotate(event) {
    const raycaster=getRaycaster?.();
    const camera=getCamera?.();
    if (!raycaster || !camera) return;
    updateMouseCoordinates(event);
    raycaster.setFromCamera(getMouse?.(),camera);
    const intersects=raycaster.intersectObjects(activeBoxes(),true);
    if (!intersects.length) return;
    event.preventDefault();
    let root=intersects[0].object;
    while (root.parent && !activeBoxes().includes(root)) root=root.parent;
    rotateThreeDimensions(root);
  }

  function bindRendererEvents() {
    if (bound) return;
    const renderer=getRenderer?.();
    if (!renderer?.domElement) return;
    bound=true;
    renderer.domElement.addEventListener('pointerdown',onDragPointerDown,false);
    renderer.domElement.addEventListener('pointermove',onDragPointerMove,false);
    renderer.domElement.addEventListener('pointerup',onDragPointerUp,false);
    renderer.domElement.addEventListener('contextmenu',onRightClickRotate);
    renderer.domElement.addEventListener('wheel',onMouseWheelHeightAdjust,{passive:false});
    window.addEventListener('keydown',event => { if (event.key === 'Shift') isShiftPressed=true; });
    window.addEventListener('keyup',event => { if (event.key === 'Shift') isShiftPressed=false; });
  }

  return {
    bindRendererEvents,
    updateSliderUi,
    stepComponentAxis,
    toggleFeetHeightSelector,
    adjustCameraTarget,
    toggleXRayMode,
    adjustComponentX,
    adjustComponentHeight,
    adjustComponentZ,
    flipCaseOrientation,
    rotateThreeDimensions
  };
}
